// Ported from LC2's ConcessionGlance.js (commons/components/HomeTabs/Today/Portfolio/Glance/
// ConcessionGlance.js) — same row-building off each "Concession Comps" glance row (community
// lookup for property_name/us_state, existing/offer percentages pre-formatted as "X% of
// rent" strings, total_units falling back to the community's total_units__c), same
// GlanceConcessionColumns shape (commons/constants/tableColumns.js: property_name, market
// concession, Stake+ offer, projected savings [currency, red/green], total units, state) and
// same "no data" empty state (see ./GlanceEmptyState.js).
// SIMPLIFIED: table chrome rebuilt on ../components/DataTableShell.js instead of MUI X
// DataGrid Pro (LC2's StyledTable, a licensed package this app doesn't depend on) — same
// idea as ../CampaignsTable.js/../ResidentActionFeedTable.js. Cell formatting/coloring itself
// (LC2's dataFormat/color columns) is preserved via ./GlanceCell.js + ./utils.js.
import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';

import {getCommunities} from '../../../app/store/reducers/entities';
import DataTableShell from '../components/DataTableShell';
import GlanceCell from './GlanceCell';
import GlanceEmptyState from './GlanceEmptyState';
import {getRandomValue} from './utils';

const COLUMN_SPECS = [
    {field: 'property_name', headerName: 'Property name'},
    {field: 'current_average_market_concession', headerName: 'Market Concession'},
    {field: 'current_average_stake_plus_offer', headerName: 'Stake+ Offer'},
    {field: 'projected_savings', headerName: 'Projected savings', format: 'currency', color: 'red/green'},
    {field: 'total_units', headerName: 'Total Units'},
    {field: 'us_state', headerName: 'State'}
];

const ConcessionGlance = ({data}) => {
    const communities = useSelector(getCommunities);

    const columns = useMemo(
        () =>
            COLUMN_SPECS.map((spec) => ({
                field: spec.field,
                headerName: spec.headerName,
                renderCell: (row) => <GlanceCell value={row[spec.field]} format={spec.format} color={spec.color} colorValue={spec.colorValue} />
            })),
        []
    );

    const rows = useMemo(() => {
        return (data || []).map((d) => {
            const concession = `${d.current_average_market_concession}% of rent`;
            const offer = `${d.average_existing_stake_plus_offer}% of rent`;
            const community = communities.find((c) => c.sfid === d.property_sfid);
            let usState = community?.full_address__statecode__s;

            if (!usState && community?.address__c) {
                const parts = community.address__c.split(' ');
                usState = parts[parts.length - 2];
            }

            return {
                ...d,
                id: getRandomValue(),
                property_name: community?.name || '',
                current_average_market_concession: concession,
                current_average_stake_plus_offer: offer,
                us_state: usState,
                total_units: d.total_units || community?.total_units__c || 0
            };
        });
    }, [data, communities]);

    if (rows.length === 0) {
        return <GlanceEmptyState msg="No concession data found" type="Glance Metric" />;
    }

    return (
        <DataTableShell
            title="Concession Comps"
            description="See what your comps are offering as a concession on new leases. All concessions are normalized to be displayed as a percentage of rent."
            columns={columns}
            rows={rows}
            getRowId={(row) => row.id}
            searchPlaceholder="Search Concession Comps"
        />
    );
};

ConcessionGlance.propTypes = {
    data: PropTypes.array
};

export default ConcessionGlance;
